import {
  Box,
  css,
  Dialog,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
  TextField,
  Button,
  Alert,
} from '@mui/material';
import { FC, useContext, useState } from 'react';
import { staticFileLocation } from '@server/constants';
import { bold, semibold } from '@styles/fontWeights';
import { LoadingButton } from '@mui/lab';
import SendOutlinedIcon from '@mui/icons-material/SendOutlined';
import IosShareIcon from '@mui/icons-material/IosShare';
import { useForm, Controller } from 'react-hook-form';
import { authContext } from '@staffedupusa/shared-components';
import { useReviewRequestCall } from '@features/profile/hooks/useReviewRequestCall';
import { toast } from 'react-toastify';
import { useLogout } from '@features/applications/hooks/useLogout';
import LogoutOutlinedIcon from '@mui/icons-material/LogoutOutlined';

type FlagCompanyModalProps = {
  isCompanyFlagModal: boolean;
  handleClose: () => void;
};

type FormData = {
  name: string;
  phone: string;
  best_time: string;
};

const FlagCompanyModal: FC<FlagCompanyModalProps> = ({
  isCompanyFlagModal,
  handleClose,
}) => {
  const { authData } = useContext(authContext);
  const [isLoading, setLoading] = useState<Boolean>(false);
  const [showValidationForm, setShowValidationForm] = useState(false);
  const [errorText, setErrorText] = useState('');
  const { control, handleSubmit, reset } = useForm<FormData>();
  const { createReviewRequest, isReviewRequesting } = useReviewRequestCall({
    setErrorText,
  });
  const handleLogout = async (event: { preventDefault: () => void }) => {
    event.preventDefault();
    await useLogout({ setLoading });
  };

  const handleSubmitRequest = (manual: boolean, data?: FormData) => {
    const onSuccess = (response: { status: number; message: string }) => {
      if (response.status === 200) {
        setErrorText('');
        reset();
        setShowValidationForm(false);
        toast.success(response.message);
      }
    };

    const options = { onSuccess };

    let payload;

    if (manual) {
      payload = {
        manual: true,
        company_name: authData?.user?.company?.s_name,
        email: authData?.user?.email,
      };
    } else {
      payload = {
        manual: false,
        ...data,
      };
    }

    createReviewRequest(payload, options);
  };

  const onSubmit = (data: FormData) => handleSubmitRequest(false, data);

  return (
    <Dialog
      open={isCompanyFlagModal}
      maxWidth="xs"
      fullWidth
      disableEnforceFocus
      sx={{ background: 'transparent' }}
    >
      <DialogTitle>
        <Box display="flex" justifyContent="center" alignItems="center">
          <img
            src={`${staticFileLocation}img/logos/TwoColorLogo.png`}
            alt="Logo"
            css={css`
              max-height: 60px;
              display: block;
            `}
          />
        </Box>
      </DialogTitle>
      <DialogContent>
        {errorText && <Alert severity="error">{errorText}</Alert>}
        {!showValidationForm ? (
          <>
            <Stack sx={{ px: 5 }} alignItems="center" pb={4} spacing={2}>
              <Typography fontWeight={bold} variant="h3">
                Uh Oh!
              </Typography>
              <Typography fontWeight={semibold} textAlign="center" variant="h4">
                Looks like your account has been flagged for review.
              </Typography>
              <Typography
                maxWidth="330px"
                textAlign="center"
                color="text.secondary"
              >
                No need to worry, we only do this to protect your business and
                the people who want to work for you.
              </Typography>
              <Typography textAlign="center" color="text.secondary">
                To get this account live and accepting applications quickly, you
                can request a manual review, or request a phone call to verify
                your business. You’re also welcome to email us at{' '}
                <a href="mailto:support@staffedup.com">support@staffedup.com</a>
              </Typography>
            </Stack>
            <Stack mb={3} spacing={2}>
              <LoadingButton
                variant="contained"
                color="primary"
                startIcon={<SendOutlinedIcon />}
                onClick={() => handleSubmitRequest(true)}
                loading={isReviewRequesting}
              >
                Request Manual Review
              </LoadingButton>
              <Button
                variant="contained"
                color="secondary"
                startIcon={<IosShareIcon />}
                disabled={isReviewRequesting}
                onClick={() => setShowValidationForm(true)}
              >
                Request Validation Call
              </Button>
              <LoadingButton
                loading={Boolean(isLoading)}
                disabled={Boolean(isLoading)}
                variant="contained"
                color="error"
                startIcon={<LogoutOutlinedIcon />}
                onClick={(e) => handleLogout(e)}
              >
                Logout
              </LoadingButton>
            </Stack>
          </>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)}>
            <Stack spacing={3} mb={2} mt={2}>
              <Controller
                name="name"
                control={control}
                defaultValue=""
                rules={{ required: 'Name is required' }}
                render={({ field, fieldState }) => (
                  <TextField
                    label="Name"
                    {...field}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    size="small"
                    fullWidth
                  />
                )}
              />
              <Controller
                name="phone"
                control={control}
                defaultValue=""
                rules={{
                  required: 'Phone number is required',
                  pattern: {
                    value: /^\d{10}$/,
                    message: 'Phone number must be exactly 10 digits',
                  },
                }}
                render={({ field, fieldState }) => (
                  <TextField
                    label="Phone Number"
                    {...field}
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    fullWidth
                    inputProps={{ maxLength: 10, inputMode: 'numeric' }}
                    size="small"
                    onInput={(e: React.ChangeEvent<HTMLInputElement>) => {
                      e.target.value = e.target.value.replace(/\D/g, '');
                      field.onChange(e);
                    }}
                  />
                )}
              />

              <Controller
                name="best_time"
                control={control}
                defaultValue=""
                rules={{ required: 'Best Time is required' }}
                render={({ field, fieldState }) => (
                  <TextField
                    label="Best Time to Call"
                    {...field}
                    fullWidth
                    error={!!fieldState.error}
                    helperText={fieldState.error?.message}
                    size="small"
                  />
                )}
              />
            </Stack>
            <Stack mt={2} direction="row" spacing={2} justifyContent="flex-end">
              <Button
                variant="outlined"
                disabled={isReviewRequesting}
                onClick={() => {
                  setShowValidationForm(false);
                  reset();
                }}
              >
                Cancel
              </Button>
              <LoadingButton
                type="submit"
                variant="contained"
                color="secondary"
                loading={isReviewRequesting}
              >
                Submit Request
              </LoadingButton>
            </Stack>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default FlagCompanyModal;
